/* global BigInt */
// src/clientQuote.js
// Pure, tested math and helpers for the client version of a quote. No window, no database.
//
// MONEY RULES (all in whole cents, integer math, see src/money.js and src/quoteMath.js):
//  1. A line's client price is its cost plus the markup for its category: cost x (100 + markup%) / 100,
//     rounded ONCE to the cent (half away from zero, so a credit line rounds like a charge line).
//  2. If the owner typed a client price for a line, that typed amount is used instead (an "override").
//  3. Subtotal = the sum of the line client prices, exactly. Nothing else is added or rounded.
//  4. Sales tax (only when switched on) = (sum of the TAXABLE lines' client prices, never below 0) x rate,
//     rounded ONCE to the cent, half up. It is its own line before the total.
//  5. Client total = subtotal + tax, so the total always equals the sum of the printed lines plus tax.
//  6. Sales tax is not revenue. Internal revenue, profit and margin never include tax.
//  7. Internal expected revenue: when the owner uses client pricing (any markup above 0 or any typed line
//     price), expected revenue IS the client subtotal (before tax). Otherwise the revenue they typed stays.

import { parseAmountText, lineCents, textCents } from "./quoteMath";

export const COST_CATEGORIES = ["Materials", "Labor", "Subcontractor", "Other"];

// Which categories are taxed by default. The owner can change any single line.
export const TAXABLE_BY_DEFAULT = { Materials: true, Labor: false, Subcontractor: false, Other: false };

export const MAX_MARKUP_PERCENT = 1000;
export const MAX_TAX_RATE_PERCENT = 30;
export const MAX_PERCENT_DECIMALS = 3;

// Reads a typed percent ("6.25", "6.25%", " 15 "). Returns { ok, num, scale, text } where the percent is
// num / 10^scale (exact), and text is the cleaned plain text. Empty means 0. Negative, more than 3
// decimals, letters or a value above max are not ok.
export function parsePercent(input, max = MAX_MARKUP_PERCENT) {
  const bad = { ok: false, num: 0n, scale: 0, text: "" };
  if (input === null || input === undefined) return { ok: true, num: 0n, scale: 0, text: "0" };
  let t = String(input).replace(/%/g, "").trim();
  if (t.includes("$") || t.includes(",")) return bad;
  if (/\.\d*$/.test(t)) t = t.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
  const p = parseAmountText(t);
  if (!p.ok || p.num < 0n || p.scale > MAX_PERCENT_DECIMALS) return bad;
  if (p.num > BigInt(max) * 10n ** BigInt(p.scale)) return bad;
  return { ok: true, num: p.num, scale: p.scale, text: t === "" ? "0" : t };
}

// n / d rounded to a whole number, half away from zero (d > 0).
function roundDiv(n, d) {
  const abs = n < 0n ? -n : n;
  let q = abs / d;
  if ((abs % d) * 2n >= d) q += 1n;
  return n < 0n ? -q : q;
}

// Cost plus markup, in whole cents, rounded once. markupText is the owner's percent text.
export function markedUpCents(costCents, markupText) {
  const m = parsePercent(markupText);
  if (!m.ok || m.num === 0n) return costCents;
  const den = 100n * 10n ** BigInt(m.scale);
  return Number(roundDiv(BigInt(costCents) * (den + m.num), den));
}

// Sales tax in whole cents: base x rate%, rounded once, half up. A base of 0 or less gives 0.
export function salesTaxCents(taxableCents, rateText) {
  const r = parsePercent(rateText, MAX_TAX_RATE_PERCENT);
  if (!r.ok || r.num === 0n || taxableCents <= 0) return 0;
  return Number(roundDiv(BigInt(taxableCents) * r.num, 100n * 10n ** BigInt(r.scale)));
}

// A line is taxable when the owner ticked it either way, otherwise by its category.
export function isLineTaxable(line) {
  if (line && typeof line.taxable === "boolean") return line.taxable;
  return !!TAXABLE_BY_DEFAULT[line && line.category];
}

export function isOverridden(line) {
  return !!line && line.clientPrice !== undefined && line.clientPrice !== null && String(line.clientPrice).trim() !== "";
}

// Everything the client sees, in whole cents.
// options: { markups: { Materials: "20", ... }, taxOn: boolean, taxRate: "6.25" }
// Returns { lines: [{ index, costCents, priceCents, overridden, taxable }], subtotalCents, taxableCents,
//           taxCents, totalCents, usesClientPricing }.
export function clientQuoteTotals(costLines, options = {}) {
  const markups = options.markups || {};
  const other = COST_CATEGORIES[COST_CATEGORIES.length - 1];
  const lines = (costLines || []).map((l, index) => {
    const costCents = lineCents(l);
    const overridden = isOverridden(l);
    const cat = COST_CATEGORIES.includes(l.category) ? l.category : other;
    const priceCents = overridden ? textCents(l.clientPrice) : markedUpCents(costCents, markups[cat]);
    return { index, costCents, priceCents, overridden, taxable: isLineTaxable(l) };
  });
  const subtotalCents = lines.reduce((s, l) => s + l.priceCents, 0);
  const taxableCents = lines.reduce((s, l) => s + (l.taxable ? l.priceCents : 0), 0);
  const taxCents = options.taxOn ? salesTaxCents(taxableCents, options.taxRate) : 0;
  const anyMarkup = COST_CATEGORIES.some((c) => { const m = parsePercent(markups[c]); return m.ok && m.num !== 0n; });
  return {
    lines, subtotalCents, taxableCents, taxCents,
    totalCents: subtotalCents + taxCents,
    usesClientPricing: anyMarkup || lines.some((l) => l.overridden),
  };
}

// Fields the owner typed that can't be read: { markups: [category,...], lines: [index,...], rate: boolean, any }.
// The tax rate only counts when tax is on.
export function clientProblems(costLines, options = {}) {
  const markups = options.markups || {};
  const badMarkups = COST_CATEGORIES.filter((c) => !parsePercent(markups[c]).ok);
  const badLines = [];
  (costLines || []).forEach((l, i) => {
    if (isOverridden(l)) { const p = parseAmountText(l.clientPrice); if (!p.ok || p.scale > 2) badLines.push(i); }
  });
  const rate = !!options.taxOn && !parsePercent(options.taxRate, MAX_TAX_RATE_PERCENT).ok;
  return { markups: badMarkups, lines: badLines, rate, any: badMarkups.length > 0 || badLines.length > 0 || rate };
}

// "6.250" -> "6.25", "" -> "0". For showing a rate on the PDF.
export function formatPercentText(text) {
  const p = parsePercent(text, 100);
  return p.ok ? p.text : "";
}

// Is tax switched on but no usable rate typed (empty or 0)? The client PDF should not print a tax line of $0.
export function taxRateMissing(taxOn, taxRate) {
  if (!taxOn) return false;
  const r = parsePercent(taxRate, MAX_TAX_RATE_PERCENT);
  return r.ok && r.num === 0n;
}

// Cents as plain dollar text for an input ("1250.50").
export function centsToText(cents) {
  return (cents / 100).toFixed(2);
}

// ── Saved shape ──────────────────────────────────────────────────────────────

// Per-line client fields, kept on the cost line itself (clientPrice as text, taxable as true/false).
export function lineFromSaved(l) {
  const out = {
    description: l.description || "", category: l.category || "Materials",
    amount: l.amount ? String(l.amount) : "", mode: l.mode || "flat",
    unitCost: l.unitCost ? String(l.unitCost) : "", quantity: l.quantity ? String(l.quantity) : "",
  };
  if (l.clientPrice !== undefined && l.clientPrice !== null && String(l.clientPrice) !== "") out.clientPrice = String(l.clientPrice);
  if (typeof l.taxable === "boolean") out.taxable = l.taxable;
  return out;
}

export function lineToSaved(l, amount, withClient) {
  const out = { description: l.description, category: l.category, amount, mode: l.mode || "flat", unitCost: l.unitCost || "", quantity: l.quantity || "" };
  if (withClient) {
    if (isOverridden(l)) out.clientPrice = String(l.clientPrice).trim();
    if (typeof l.taxable === "boolean") out.taxable = l.taxable;
  }
  return out;
}

export function blankMarkups() {
  return COST_CATEGORIES.reduce((m, c) => ({ ...m, [c]: "" }), {});
}

// Reads saved client options (estimates.client_options or contractors.quote_defaults) into safe values.
export function readClientOptions(raw) {
  const o = raw && typeof raw === "object" ? raw : {};
  const markups = blankMarkups();
  COST_CATEGORIES.forEach((c) => {
    const v = o.markups && o.markups[c];
    if (v !== undefined && v !== null && parsePercent(v).ok) markups[c] = parsePercent(v).num === 0n ? "" : String(v);
  });
  const rate = o.taxRate !== undefined && o.taxRate !== null && parsePercent(o.taxRate, MAX_TAX_RATE_PERCENT).ok ? String(o.taxRate) : "";
  return {
    markups,
    taxOn: o.taxOn === true,
    taxState: typeof o.taxState === "string" && /^[A-Z]{2}$/.test(o.taxState) ? o.taxState : "",
    taxRate: rate,
    validUntil: typeof o.validUntil === "string" && isIsoDate(o.validUntil) ? o.validUntil : "",
    clientNotes: typeof o.clientNotes === "string" ? o.clientNotes.slice(0, 2000) : "",
  };
}

// What is saved on the estimate (estimates.client_options).
export function buildClientOptions(s) {
  return { version: 1, markups: s.markups, taxOn: !!s.taxOn, taxState: s.taxState || "", taxRate: s.taxRate || "", validUntil: s.validUntil || "", clientNotes: s.clientNotes || "" };
}

// What is remembered for the business (contractors.quote_defaults): markups, state and rate only.
export function buildQuoteDefaults(s) {
  return { markups: s.markups, taxState: s.taxState || "", taxRate: s.taxRate || "" };
}

// ── Dates (plain YYYY-MM-DD text; no time zones involved) ────────────────────

export function isIsoDate(t) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t || "");
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}

export function todayIso(now = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export function addDaysIso(iso, days) {
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  const p = (n) => String(n).padStart(2, "0");
  return `${t.getUTCFullYear()}-${p(t.getUTCMonth() + 1)}-${p(t.getUTCDate())}`;
}

export const DEFAULT_VALID_DAYS = 30;

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export function formatIsoLong(iso) {
  if (!isIsoDate(iso)) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

// True when a save failed only because the estimates table has no client_options column yet
// (the migration is not applied). PostgREST says "Could not find the 'client_options' column"; Postgres says
// 'column "client_options" ... does not exist'.
export function isMissingClientColumn(error) {
  return !!error && /client_options/.test(String(error.message || ""));
}
